import {
  ContractInvoiceType,
  ContractStatus,
} from "@boark/shared/types/contract";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";

import { useGetContracts } from "../queries/contract";
import { useGetOrganizations } from "../queries/organization";
import { uniqueSortedTagSuggestions } from "../services/contract";
import { formatOrganizationLabel } from "../services/organization";
import { useLocale } from "../stores/locale";
import { recordIdToId } from "../utils/record-id-to-id";
import { Button } from "./button";
import { Input } from "./input";
import { Select } from "./select";
import { TagInput } from "./tag-input";

export type ContractFormValues = {
  title: string;
  description: string;
  tags: string[];
  status: ContractStatus;
  cost: string;
  invoiceType: ContractInvoiceType | "";
  fromDate: string;
  toDate: string;
  counterPartyId: string;
};

export function ContractForm({
  onCancel,
  onChange,
  onSubmit,
  values,
  loading = false,
  disabled = false,
  hasError = false,
}: {
  onCancel: () => void;
  onChange: (values: ContractFormValues) => void;
  onSubmit: () => void;
  values: ContractFormValues;
  loading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
}) {
  const t = useLocale((state) => state.locale);
  const { data: organizations } = useGetOrganizations();
  const { data: contracts } = useGetContracts();

  const tagSuggestions = useMemo(
    () => uniqueSortedTagSuggestions(contracts),
    [contracts],
  );

  const isValid = useMemo(() => {
    return values.title.trim() !== "" && values.fromDate.trim() !== "";
  }, [values.title, values.fromDate]);

  const isDisabled = useMemo(() => {
    return disabled || loading;
  }, [disabled, loading]);

  const counterPartySelectValue = useMemo(() => {
    if (values.counterPartyId === "" || !organizations) {
      return values.counterPartyId;
    }

    const match = organizations.find(
      (organization) =>
        organization.id === values.counterPartyId ||
        recordIdToId(organization.id) === values.counterPartyId ||
        recordIdToId(organization.id) === recordIdToId(values.counterPartyId),
    );

    return match?.id ?? values.counterPartyId;
  }, [values.counterPartyId, organizations]);

  return (
    <form
      className="flex-1 flex flex-col justify-between border-1 border-gray-300 rounded-xs p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <Input
          required
          className="col-span-2"
          disabled={isDisabled}
          label={t.contract.title}
          type="text"
          value={values.title}
          onChange={(e) => onChange({ ...values, title: e.target.value })}
        />
        <div className="col-span-2">
          <label
            className={`block text-sm font-medium ${isDisabled ? "text-gray-400" : "text-gray-500"}`}
          >
            {t.contract.description}
          </label>
          <textarea
            className="w-full border-1 border-gray-300 rounded-xs p-2 min-h-[4.5rem] bg-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            disabled={isDisabled}
            value={values.description}
            onChange={(e) =>
              onChange({ ...values, description: e.target.value })
            }
          />
        </div>
        <div className="col-span-2 w-full">
          <TagInput
            disabled={isDisabled}
            hint={t.contract.tagsHint}
            label={t.contract.tags}
            placeholder={t.contract.tagsPlaceholder}
            suggestions={tagSuggestions}
            value={values.tags}
            onChange={(tags) => onChange({ ...values, tags })}
          />
        </div>
        <Select
          label={t.contract.status}
          options={[
            { label: t.contract.statusActive, value: ContractStatus.ACTIVE },
            {
              label: t.contract.statusInactive,
              value: ContractStatus.INACTIVE,
            },
          ]}
          value={values.status}
          onChange={(value) =>
            onChange({ ...values, status: value as ContractStatus })
          }
        />
        <Input
          disabled={isDisabled}
          label={t.contract.cost}
          type="number"
          value={values.cost}
          onChange={(e) => onChange({ ...values, cost: e.target.value })}
        />
        <Select
          allowEmpty
          label={t.contract.invoiceType}
          options={[
            {
              label: t.contract.invoiceTypeMonthly,
              value: ContractInvoiceType.MONTHLY,
            },
            {
              label: t.contract.invoiceTypeQuarterly,
              value: ContractInvoiceType.QUARTERLY,
            },
            {
              label: t.contract.invoiceTypeSemiAnnually,
              value: ContractInvoiceType.SEMI_ANNUALLY,
            },
            {
              label: t.contract.invoiceTypeAnnually,
              value: ContractInvoiceType.ANNUALLY,
            },
          ]}
          placeholder={t.select.placeholder}
          value={values.invoiceType}
          onChange={(value) =>
            onChange({
              ...values,
              invoiceType: value as ContractInvoiceType | "",
            })
          }
        />
        <Select
          allowEmpty
          label={t.contract.counterParty}
          options={
            organizations?.map((organization) => ({
              label: formatOrganizationLabel(organization),
              value: organization.id,
            })) ?? []
          }
          placeholder={t.select.placeholder}
          value={counterPartySelectValue}
          onChange={(value) => onChange({ ...values, counterPartyId: value })}
        />
        <Input
          required
          disabled={isDisabled}
          label={t.contract.fromDate}
          max={values.toDate}
          type="date"
          value={values.fromDate}
          onChange={(e) => onChange({ ...values, fromDate: e.target.value })}
        />
        <Input
          disabled={isDisabled || values.fromDate.trim() === ""}
          label={t.contract.toDate}
          min={values.fromDate}
          type="date"
          value={values.toDate}
          onChange={(e) => onChange({ ...values, toDate: e.target.value })}
        />
      </div>
      <div className="flex gap-2 justify-end mt-4">
        {hasError && (
          <p className="text-red-500 flex items-center gap-2">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            {t.contract.error}
          </p>
        )}
        <Button disabled={disabled} severity="secondary" onClick={onCancel}>
          {t.cancel}
        </Button>
        <Button disabled={disabled || !isValid} loading={loading} type="submit">
          {t.page.save}
        </Button>
      </div>
    </form>
  );
}
